import { getRTRLocale } from '../config';
import { consoleWarn } from './consoleActions';

/** The locale code (ISO 639-1 + optional country code) */
export type LocaleCode = string;
export type ValidLanguages = {
  id: string;
  code: string;
};

export const defaultFallbackLocaleCode: LocaleCode = 'en-GB';

// mimic the language options from rentware backend to support two letter language codes
const defaultLocaleMap = {
  ca: 'ca-ES',
  cs: 'cs-CZ',
  da: 'da-DK',
  // de: 'de-AT', // de will be mapped to de-DE
  // de: 'de-CH', // de will be mapped to de-DE
  de: 'de-DE',
  en: 'en-GB',
  // en: 'en-US', // en will be mapped to en-GB
  es: 'es-ES',
  et: 'et-EE',
  fi: 'fi-FI',
  // fr: 'fr-BE', // fr will be mapped to fr-FR
  fr: 'fr-FR',
  hr: 'hr-HR',
  it: 'it-IT',
  lt: 'lt-LT',
  lv: 'lv-LV',
  nb: 'nb-NO',
  // nl: 'nl-BE',
  nl: 'nl-NL',
  pl: 'pl-PL',
  pt: 'pt-PT',
  ro: 'ro-RO',
  sv: 'sv-SE',
  tr: 'tr-TR',
};

export function useAutoLanguageChange() {
  if ((window as any).RTR_DISABLE_AUTO_LANGUAGE) {
    return false;
  }

  const locale = getRTRLocale();

  if (!locale) {
    return true;
  }

  const localeHasCorrectFormat = locale.split('-').length === 2;

  return !localeHasCorrectFormat;
}

export function getLocaleFromHtmlLang() {
  const htmlElement = document.documentElement;
  return htmlElement.lang || defaultFallbackLocaleCode;
}

export function getLocaleWithCountryCode(locale: string) {
  if (!locale || typeof locale !== 'string') {
    consoleWarn('Locale is not a string');

    return null;
  }

  let [langCode, countryCode] = locale.split('-');
  langCode = langCode.toLowerCase();

  if (countryCode) {
    countryCode = countryCode.toUpperCase();
  } else if (!!defaultLocaleMap[langCode]) {
    return defaultLocaleMap[langCode];
  } else {
    return null;
  }

  return `${langCode}-${countryCode}`;
}

/**
 * Fallback to the closest locale if the current locale is not valid
 * @param locale - The current locale
 * @param validLanguages - The list of valid languages
 * @param defaultLocale - The default locale
 * @returns The closest valid locale or the default fallback locale
 * @example
 * fallbackToClosestLocale('en', ['en-GB', 'en-US']) // 'en-GB'
 * fallbackToClosestLocale('de-AT', ['en-GB', 'en-US']) // 'en-GB'
 * fallbackToClosestLocale('de-AT', ['de-CH', 'en-US']) // 'en-CH'
 * fallbackToClosestLocale('de-AT', ['de-CH', 'de-DE']) // 'en-DE'
 * fallbackToClosestLocale('en-US', ['en-GB', 'en-US']) // 'en-US'
 * fallbackToClosestLocale('en-US', ['en-GB', 'de-DE']) // 'en-GB'
 */
function getClosestLocale(locale: string, validLanguages: ValidLanguages[], defaultLocale: string) {
  if (!locale || !validLanguages || validLanguages.length === 0) {
    consoleWarn('Locale or valid languages not set');
    return defaultLocale || defaultFallbackLocaleCode;
  }

  const [langCode] = locale.split('-');

  if (langCode === 'de') {
    const deDeIsValid = validLanguages.find(item => item.code === defaultLocaleMap['de']);
    if (deDeIsValid) {
      return defaultLocaleMap['de'];
    }

    const firstValidLanguage = validLanguages.find(item => item.code.startsWith('de'));
    if (firstValidLanguage) {
      return firstValidLanguage.code;
    }
  }

  if (langCode === 'en') {
    const enGbIsValid = validLanguages.find(item => item.code === defaultLocaleMap['en']);
    if (enGbIsValid) {
      return defaultLocaleMap['en'];
    }

    const firstValidLanguage = validLanguages.find(item => item.code.startsWith('en'));
    if (firstValidLanguage) {
      return firstValidLanguage.code;
    }
  }

  return defaultLocale || defaultFallbackLocaleCode;
}

export function getLocaleWithReasonableFallback(locale: string, validLanguages: ValidLanguages[], defaultLocale: string | null) {
  if (isLocaleValid(locale, validLanguages)) {
    return locale;
  }

  return getClosestLocale(locale, validLanguages, defaultLocale);
}

export function isLocaleValid(locale: string, validLanguages: ValidLanguages[]) {
  if (!locale || !validLanguages) {
    consoleWarn('Locale or valid languages not set');
    return false;
  }

  if (validLanguages.length === 0) {
    consoleWarn('No valid languages set');
    return false;
  }

  const localeWithCountryCode = getLocaleWithCountryCode(locale);

  if (validLanguages.findIndex(item => compareLocaleByCountryCode(item.code, localeWithCountryCode)) === -1) {
    return false;
  }
  return true;
}

function compareLocaleByCountryCode(locale1: string, locale2: string) {
  if (!locale1 || !locale2) {
    consoleWarn('Locale is not a string');
    return false;
  }

  const locale1CountryCode = locale1?.split('-')[1];
  const locale2CountryCode = locale2?.split('-')[1];

  if (!locale1CountryCode || !locale2CountryCode) {
    consoleWarn('Locale country code is missing. Format should be like en-GB, de-DE, etc.');
    return false;
  }

  return locale1CountryCode === locale2CountryCode;
}
